interface MethodsSectionProps {
  scrollTo: (id: string) => void;
}

export default function MethodsSection({ scrollTo }: MethodsSectionProps) {
  const methods = [
    { icon: 'ri-user-voice-line', title: 'Ролевые игры', desc: 'Моделирование реальных рабочих ситуаций для отработки навыков в безопасной среде. Участники получают обратную связь и могут скорректировать поведение.' },
    { icon: 'ri-gamepad-line', title: 'Бизнес-игры', desc: 'Командные симуляции бизнес-процессов, развивающие стратегическое мышление, навыки переговоров и принятия решений в условиях ограниченных ресурсов.' },
    { icon: 'ri-file-list-3-line', title: 'Кейсы', desc: 'Разбор реальных ситуаций из практики компании или отрасли. Участники анализируют проблему и предлагают решения на основе полученных знаний.' },
    { icon: 'ri-computer-line', title: 'Симуляции', desc: 'Компьютерные или настольные модели бизнес-ситуаций, где участники видят последствия своих решений в режиме реального времени.' },
    { icon: 'ri-group-line', title: 'Фасилитация', desc: 'Структурированное обсуждение, в котором группа под руководством фасилитатора находит оптимальные решения бизнес-задач и достигает консенсуса.' },
    { icon: 'ri-chat-check-line', title: 'Разбор переговоров', desc: 'Анализ аудио- и видеозаписей реальных переговоров с детальной обратной связью: что сработало, что нет, как улучшить.' },
    { icon: 'ri-user-follow-line', title: 'Наставничество', desc: 'Передача опыта от старших сотрудников младшим через совместную работу, регулярную обратную связь и индивидуальный план развития.' },
    { icon: 'ri-psychotherapy-line', title: 'Коучинг', desc: 'Индивидуальная работа с руководителями и ключевыми сотрудниками: постановка целей, раскрытие потенциала, преодоление внутренних барьеров.' },
    { icon: 'ri-feedback-line', title: 'Обратная связь', desc: 'Система регулярной обратной связи 360 градусов: от руководителя, коллег и подчинённых. Помогает сотрудникам видеть зоны роста.' },
    { icon: 'ri-smartphone-line', title: 'Микрообучение', desc: 'Короткие обучающие модули по 5-10 минут для закрепления навыков и поддержания знаний между основными этапами обучения.' }
  ];

  const evaluationMetrics = [
    { icon: 'ri-percent-line', title: 'Конверсия', desc: 'Процент успешных сделок от общего числа обращений. Рост конверсии — прямой показатель эффективности обучения продажам.' },
    { icon: 'ri-money-dollar-circle-line', title: 'Средний чек', desc: 'Увеличение среднего чека после обучения техникам допродаж и кросс-продаж.' },
    { icon: 'ri-emotion-happy-line', title: 'Удовлетворённость клиентов', desc: 'Индекс NPS, отзывы, количество повторных обращений — индикаторы качества сервиса после обучения.' },
    { icon: 'ri-repeat-line', title: 'Повторные продажи', desc: 'Доля клиентов, возвращающихся за повторной покупкой. Растёт, когда обучение улучшает клиентский опыт.' },
    { icon: 'ri-timer-line', title: 'Время адаптации новичков', desc: 'Сокращение времени выхода нового сотрудника на плановые показатели — результат системного обучения.' },
    { icon: 'ri-close-line', title: 'Ошибки сотрудников', desc: 'Снижение количества ошибок и рекламаций после внедрения стандартов работы через обучение.' },
    { icon: 'ri-heart-pulse-line', title: 'Вовлечённость', desc: 'Рост индекса вовлечённости сотрудников (eNPS) — люди ценят, когда компания инвестирует в их развитие.' }
  ];

  const mistakes = [
    { title: 'Учить всех одинаково', desc: 'У разных отделов и сотрудников — разные потребности. Единая программа для всех снижает эффективность обучения.' },
    { title: 'Нет диагностики', desc: 'Без предварительного анализа потребностей обучение не попадает в реальные проблемы бизнеса.' },
    { title: 'Нет KPI', desc: 'Без измеримых показателей невозможно оценить эффективность обучения и его влияние на бизнес.' },
    { title: 'Нет поддержки руководства', desc: 'Если руководители не участвуют в обучении и не поддерживают изменения, результат будет нулевым.' },
    { title: 'Нет практики', desc: 'Теория без практической отработки не формирует навык — сотрудники не могут применить знания в работе.' },
    { title: 'Нет контроля', desc: 'После обучения никто не проверяет, применяют ли сотрудники новые навыки. Изменения не закрепляются.' },
    { title: 'Обучение ради галочки', desc: 'Формальный подход: провели тренинг, поставили галочку — и забыли. Реального влияния на бизнес нет.' },
    { title: 'Слишком много теории', desc: 'Перегруз теоретической информацией без привязки к реальным задачам — знания быстро забываются.' },
    { title: 'Игнорирование руководителей', desc: 'Обучение только рядовых сотрудников без развития управленческих компетенций руководителей.' },
    { title: 'Нет оценки результатов', desc: 'Отсутствие посттренинговой оценки не позволяет понять, окупились ли инвестиции в обучение.' }
  ];

  return (
    <>
      {/* Современные методы обучения сотрудников */}
      <section id="methods" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Современные методы обучения сотрудников
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Эффективное развитие персонала использует разнообразные инструменты для достижения устойчивых результатов
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {methods.map((method, i) => (
              <div key={i} className="bg-gray-50 p-6 rounded-xl hover:bg-white hover:ring-2 hover:ring-teal-200 transition-all group">
                <div className="w-11 h-11 bg-teal-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <i className={`${method.icon} text-lg text-white`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{method.title}</h3>
                <p className="text-gray-600 text-xs leading-relaxed">{method.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Как оценить эффективность обучения */}
      <section className="py-20 md:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            Как оценить эффективность обучения
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Ключевые показатели, по которым измеряется реальное влияние обучения на бизнес
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {evaluationMetrics.map((metric, i) => (
              <div key={i} className="bg-white p-7 rounded-xl text-center hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${metric.icon} text-2xl text-teal-600`}></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{metric.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{metric.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button onClick={() => scrollTo('contact')} className="bg-teal-600 text-white px-8 py-4 rounded-lg text-base font-semibold hover:bg-teal-700 transition-all whitespace-nowrap cursor-pointer">
              Получить консультацию
            </button>
          </div>
        </div>
      </section>

      {/* ТОП-10 ошибок корпоративного обучения */}
      <section id="mistakes" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-5xl font-bold text-gray-900 text-center mb-6">
            ТОП-10 ошибок корпоративного обучения
          </h2>
          <p className="text-lg text-gray-600 text-center mb-16 max-w-3xl mx-auto">
            Избегайте этих распространённых ошибок, чтобы обучение приносило реальные бизнес-результаты
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 max-w-6xl mx-auto">
            {mistakes.map((mistake, i) => (
              <div key={i} className="bg-gray-50 p-6 rounded-xl border-l-4 border-red-400 hover:border-teal-400 transition-colors">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-7 h-7 bg-gray-900 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                  <h3 className="font-bold text-gray-900 text-sm">{mistake.title}</h3>
                </div>
                <p className="text-gray-600 text-xs leading-relaxed">{mistake.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}